import {
  getAllDemandMiniatures,
  getDefaultDemandMiniatures,
} from '../../helpers/DemandMiniatureDefaults';
import { isSoilLayer } from './PrescriptionJob';
import {
  getDemandTypeForLayer,
  isDemandLayerType,
} from '../../model/surveys/country/SurveyCountryStrategy';
import {
  calculateClassificationIntervalAreas,
  calculateClassificationIntervals,
  calculateIntervalAreas,
  calculateIntervals,
  calculateSoilIntervals,
  initializeOverrides,
} from './PrescriptionUtils';

export type DemandClassification = {
  values: Array<Array<number | null>>;
  mappings: Record<string, Record<string, number[]>>;
};

type DemandMap = {
  sourceLayer: string;
  classificationsEnabled?: boolean;
  variationsEnabled: boolean;
  multipleSelections?: string[];
};

type DemandJob = {
  layer: string;
  layerType?: string | null;
  classifications?: DemandClassification;
};

type BulkJob = {
  fieldId: number;
  values: Array<Array<number | null>>;
  intervals: Array<{ min: number; max: number; prescription?: number }>;
};

export type BulkDemandResult = {
  classifications: DemandClassification;
  survey: { surveyImageId: number };
};

export type BulkDemandClassifications = Record<number, BulkDemandResult>;

export const getDemandMapUpdates = (
  map: DemandMap,
  enabled: boolean,
  countryCode: string | null,
  baselineSelections: string[] = []
) => {
  const currentSelections = map.multipleSelections || [];
  const demandMiniatures = getDefaultDemandMiniatures(map.sourceLayer, countryCode);
  const allDemandMiniatures = getAllDemandMiniatures(countryCode);
  const baseSelections = currentSelections.filter((layer) => !allDemandMiniatures.includes(layer));
  const multipleSelections = enabled
    ? [...baseSelections, ...demandMiniatures.filter((layer) => !baseSelections.includes(layer))]
    : currentSelections.filter(
        (layer) => !demandMiniatures.includes(layer) || baselineSelections.includes(layer)
      );

  return {
    classificationsEnabled: enabled,
    multipleSelections,
    variationsEnabled: enabled
      ? false
      : isSoilLayer(map.sourceLayer)
        ? map.variationsEnabled
        : true,
  };
};

export const getJobDemandChanges = (
  map: Pick<DemandMap, 'sourceLayer' | 'classificationsEnabled'>,
  job: DemandJob,
  countryCode: string | null,
  classificationsByLayer: Record<string, DemandClassification | undefined>
) => ({
  layerType: map.classificationsEnabled
    ? getDemandTypeForLayer(map.sourceLayer, countryCode)
    : undefined,
  classifications: map.classificationsEnabled
    ? classificationsByLayer[map.sourceLayer] ||
      (job.layer === map.sourceLayer ? job.classifications : undefined)
    : undefined,
});

export const getPrescriptionScaleModes = (
  layerType: string | null | undefined,
  map: Pick<DemandMap, 'classificationsEnabled' | 'variationsEnabled'>
) => {
  const classificationsEnabled = isDemandLayerType(layerType);
  return {
    classificationsEnabled,
    variationsEnabled:
      !classificationsEnabled && !map.classificationsEnabled && map.variationsEnabled,
  };
};

export const recalculateJobIntervals = <
  T extends DemandJob & {
    values: Array<Array<number | null>>;
    intervals: Array<{ min: number; max: number; prescription?: number }>;
    overrides?: Array<Array<number>>;
  },
>(
  job: T
): T => {
  const recalculated = { ...job, intervals: [], overrides: undefined } as T;
  recalculated.overrides = initializeOverrides(recalculated);

  if (isDemandLayerType(recalculated.layerType)) {
    recalculated.intervals = calculateClassificationIntervals(recalculated);
    return calculateClassificationIntervalAreas(recalculated) as T;
  }

  recalculated.intervals = isSoilLayer(recalculated.layer)
    ? calculateSoilIntervals(recalculated)
    : calculateIntervals(recalculated);
  return calculateIntervalAreas(recalculated) as T;
};

export const prepareBulkDemandOverview = <T extends BulkJob>(
  bulkJobs: Record<number, T>,
  classificationsByField: BulkDemandClassifications,
  firstFieldId: number
) => {
  const updatedBulkJobs: Record<
    number,
    T & {
      classifications: DemandClassification;
      surveyImageId: number;
    }
  > = {};
  Object.values(bulkJobs).forEach((job) => {
    const result = classificationsByField[job.fieldId];
    updatedBulkJobs[job.fieldId] = {
      ...job,
      classifications: result.classifications,
      surveyImageId: result.survey.surveyImageId,
    };
  });

  const firstClassification = classificationsByField[firstFieldId].classifications;
  const combinedClassifications = {
    ...firstClassification,
    values: Object.values(updatedBulkJobs).flatMap((job) => job.classifications.values),
  };

  return { updatedBulkJobs, combinedClassifications };
};
